// Analytics tab › Dashboards (spec §9): master-detail — Dashboards (D) on
// the left (with used/unused badges from the object catalog), the EXISTING
// embedded DashboardDesignerPage on the right. "New" generates a dashboard
// with AI (its visuals cascade automatically).
import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { ListPlus, CheckCircle2, Circle } from 'lucide-react'
import DashboardDesignerPage from '@/pages/designers/DashboardDesignerPage'
import DashboardPreviewModal from '@/pages/designers/dashboard/DashboardPreviewModal'
import {
  generateDashboardObject,
  saveDashboardObject,
  deleteDashboardObject,
  listProjectDashboards,
  objectCatalog,
  type CatalogObject,
  type ProjectDashboardSummary,
} from '@/api/designers'
import { blankDashboardModel } from '@/pages/designers/dashboard/helpers'
import ObjectMasterDetail, { type MasterItem } from './ObjectMasterDetail'
import PreviewThumbnail, { bustPreview } from './PreviewThumbnail'
import PublishToMenuDialog, { type PublishTarget } from './PublishToMenuDialog'
import { EmptyStateCoach, TypeChip, errMessage, slugify } from './shared'

export default function DashboardsSection({
  project, registerLeaveGuard, initialObjName, openNonce, onChanged,
}: {
  project: string
  registerLeaveGuard?: (fn: ((proceed: () => void) => void) | null) => void
  initialObjName?: string
  openNonce?: number
  onChanged?: () => void
}) {
  const [dashboards, setDashboards] = useState<ProjectDashboardSummary[]>([])
  const [catalog, setCatalog] = useState<Record<string, CatalogObject>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [menuTarget, setMenuTarget] = useState<PublishTarget | null>(null)
  // "View" from a card → the shared full-screen preview modal (loads the SAVED
  // dashboard from disk; same PreviewBody the designer Preview / navigation use).
  const [viewObj, setViewObj] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // The list gives rich meta; the catalog adds used/unused. The catalog
      // is best-effort — the list alone is enough to render.
      const [listRes, catRes] = await Promise.allSettled([
        listProjectDashboards(project),
        objectCatalog(project, 'D'),
      ])
      if (listRes.status === 'rejected') throw listRes.reason
      setDashboards(listRes.value.dashboards || [])
      if (catRes.status === 'fulfilled') {
        const map: Record<string, CatalogObject> = {}
        for (const o of catRes.value.objects || []) map[o.obj_name] = o
        setCatalog(map)
      }
    } catch (err) {
      setError(errMessage(err, 'Could not load dashboards'))
    } finally {
      setLoading(false)
    }
  }, [project])

  useEffect(() => { void refresh() }, [refresh])

  const items: MasterItem[] = dashboards.map((d) => {
    const cat = catalog[d.obj_name]
    const hasName = !!d.dashboard_name && d.dashboard_name !== d.obj_name
    return {
      id: d.obj_name,
      name: d.dashboard_name || d.obj_name,
      subtitle: hasName ? d.obj_name : undefined,
      description: d.description || undefined,
      tags: [`${d.visual_count} visual${d.visual_count === 1 ? '' : 's'}`],
      badge: cat
        ? (cat.used
            ? { text: 'On the app menu / in use', tone: 'success' as const, icon: <CheckCircle2 size={15} style={{ color: 'var(--color-success)' }} /> }
            : { text: 'Not used yet', tone: 'muted' as const, icon: <Circle size={14} style={{ color: 'var(--color-text-faint)' }} /> })
        : undefined,
    }
  })

  const handleCreate = async (name: string, description: string): Promise<string | null> => {
    const objName = slugify(name)
    try {
      const res = await generateDashboardObject({
        project,
        obj_name: objName,
        description: description || name,
      })
      const cascaded = res.cascaded_visuals?.length || 0
      const failed = res.verification?.failures?.length || 0
      if (failed > 0) {
        toast.error(
          `Dashboard "${name}" drafted but ${failed} tile${failed === 1 ? '' : 's'} still fail${failed === 1 ? 's' : ''} when run — open it and check the tiles`,
          { duration: 6000 },
        )
      } else {
        toast.success(
          `Dashboard "${name}" drafted${cascaded ? ` — ${cascaded} visual${cascaded === 1 ? '' : 's'} created with it` : ''}`,
        )
      }
      await refresh()
      onChanged?.()
      return objName
    } catch (err) {
      toast.error(errMessage(err, 'Could not generate the dashboard'))
      return null
    }
  }

  // Manual path: persist a blank dashboard (no AI) and open the designer so the
  // user arranges tiles from existing visuals themselves.
  const handleCreateManual = async (name: string, _description: string): Promise<string | null> => {
    const objName = slugify(name)
    try {
      await saveDashboardObject(project, objName, blankDashboardModel(objName))
      toast.success(`Dashboard "${name}" created — add visual tiles`)
      await refresh()
      onChanged?.()
      return objName
    } catch (err) {
      toast.error(errMessage(err, 'Could not create the dashboard'))
      return null
    }
  }

  return (
    <>
    <ObjectMasterDetail
      project={project}
      typeCode="D"
      heading="Dashboards"
      subCopy="Grids of visuals with shared filters and drill-through. Open the designer to arrange tiles, wire criteria, and set drill-through."
      items={items}
      loading={loading}
      error={error}
      onRetry={() => void refresh()}
      newLabel="New dashboard"
      newDialogTitle="New dashboard"
      namePlaceholder="e.g. Sales performance"
      descPlaceholder="e.g. KPI headline row, sales trend by month, top products — filtered by plant and date range."
      createButtonLabel="Generate with AI"
      onCreate={handleCreate}
      onCreateManual={handleCreateManual}
      manualButtonLabel="Create manually"
      registerLeaveGuard={registerLeaveGuard}
      initialObjName={initialObjName}
      openNonce={openNonce}
      renderPreview={(item) => (
        <PreviewThumbnail project={project} objName={item.id} kind="D" />
      )}
      onEditorClose={(obj) => bustPreview(project, 'D', obj)}
      onView={(obj) => setViewObj(obj)}
      deleteConfig={{
        objType: 'D',
        objLabel: 'dashboard',
        doDelete: async (id) => { await deleteDashboardObject(project, id) },
        onDeleted: () => { void refresh(); onChanged?.() },
      }}
      detailToolbar={(objName) => {
        const cat = catalog[objName]
        if (!cat || cat.in_navigation) return null
        const d = dashboards.find((x) => x.obj_name === objName)
        return (
          <button
            className="btn btn-secondary btn-sm"
            title="Wire this dashboard into the application menu — no regeneration"
            onClick={() => setMenuTarget({
              obj_name: objName,
              obj_type: 'D',
              title: d?.dashboard_name || cat.title || objName,
            })}
          >
            <ListPlus size={13} />
            Add to menu…
          </button>
        )
      }}
      emptyState={(openNew) => (
        <EmptyStateCoach
          icon={<TypeChip code="D" size={26} />}
          title="No dashboards yet"
          body="A dashboard is a grid of visuals with shared filters. Generate one from a description — its starter visuals are created along with it."
          action={
            <button className="btn btn-primary btn-sm" onClick={openNew}>
              + New dashboard
            </button>
          }
        />
      )}
      renderDesigner={(objName, common) => (
        <DashboardDesignerPage key={`${project}:${objName}`} {...common} />
      )}
    />
    <PublishToMenuDialog
      open={menuTarget !== null}
      mode="menu"
      project={project}
      targets={menuTarget ? [menuTarget] : []}
      onClose={() => setMenuTarget(null)}
      onPublished={() => { void refresh(); onChanged?.() }}
    />
    <DashboardPreviewModal
      open={viewObj !== null}
      projectName={project}
      objectName={viewObj || ''}
      onClose={() => setViewObj(null)}
    />
    </>
  )
}
